import { styled } from '@mui/system'
import { useGridFilter } from 'ag-grid-react'
import DateVO from '../../../../../../../../vo/DateVO'
import DateCellOperatorSelect, {
  DateCellFilterOperator,
  DateCellFilterOperatorLabel,
} from './dateCellOperatorSelect'
import ResetButton from '../../../../../../../components/buttons/ResetButton'
import { intl } from '../../../../../../../../i18n'
import { IDoesFilterPassParams } from 'ag-grid-community'
import DateInputWithClassVO from '../../../../../../../components/editors/input/DateInput/DateInputWithClassVO'

type DateCellFilterModel = {
  value: {
    dateValue?: string
    operator: DateCellFilterOperator
  }
}

const buildModel = (
  operator: DateCellFilterOperator,
  value?: DateVO
): DateCellFilterModel | null => {
  const isActive = operator === DateCellFilterOperator.NOT_SET || !!value
  return isActive
    ? {
        value: {
          dateValue: value ? value.toLabel() : undefined,
          operator,
        },
      }
    : null
}

const RootDiv = styled('div')({
  padding: '3px 8px',
  display: 'flex',
})
const StyledDateCellOperatorSelect = styled(DateCellOperatorSelect)({
  minWidth: 70,
  margin: 'auto 5px auto 0',
})
const StyledDateInputWithClassVO = styled(DateInputWithClassVO)({
  width: 110,
  margin: 'auto 5px auto 0',
})
const StyledResetButton = styled(ResetButton)({
  whiteSpace: 'nowrap',
  margin: 'auto 0',
})

const DateCellFilter = ({
  key,
  dateValue,
  onChangeDateValue,
  operator,
  onChangeOperator,
  clearFilter,
}: {
  key: string
  dateValue?: DateVO
  onChangeDateValue: (dateValue?: DateVO) => void
  operator: DateCellFilterOperator
  onChangeOperator: (operator: DateCellFilterOperator) => void
  clearFilter: () => void
}) => (
  <RootDiv>
    <StyledDateCellOperatorSelect
      id={`${key}-${operator}`}
      value={operator}
      onChange={onChangeOperator}
    />
    <StyledDateInputWithClassVO
      value={dateValue}
      onChange={onChangeDateValue}
      disabled={operator === DateCellFilterOperator.NOT_SET}
      isClearable={operator === DateCellFilterOperator.NOT_SET}
    />
    <StyledResetButton
      label={intl.formatMessage({ id: 'clear' })}
      onClick={clearFilter}
    />
  </RootDiv>
)

interface Props {
  colDef: {
    field: string
  }
  model: DateCellFilterModel | null
  getValue: (node: any) => any
  onModelChange: (model: DateCellFilterModel | null) => void
}

export default (props: Props) => {
  const operator =
    (props.model?.value?.operator as DateCellFilterOperator) ??
    DateCellFilterOperator.EQ
  const value = props.model?.value?.dateValue
    ? new DateVO(props.model.value.dateValue)
    : undefined

  const doesFilterPass = (params: IDoesFilterPassParams) => {
    // Don't filter new rows
    if (params.data.isAdded) return true
    const dateValue = props.getValue(params.node)
    const rowDataValue = dateValue ? new DateVO(dateValue) : undefined
    if (!rowDataValue) {
      return operator === DateCellFilterOperator.NOT_SET
    }
    if (!value) return false
    switch (operator) {
      case DateCellFilterOperator.EQ:
        return rowDataValue.isEqual(value)
      case DateCellFilterOperator.GTE:
        return rowDataValue.isSameOrAfter(value)
      case DateCellFilterOperator.LTE:
        return rowDataValue.isSameOrBefore(value)
      default:
        return false
    }
  }

  const getModelAsString = () => {
    if (!props.model) return ''
    return (
      DateCellFilterOperatorLabel[operator] +
      (value ? ` : ${value.toLabel()}` : '')
    )
  }

  useGridFilter({ doesFilterPass, getModelAsString })

  return (
    <DateCellFilter
      key={`date-cell-filter-${props.colDef.field}`}
      dateValue={value}
      operator={operator}
      onChangeOperator={op => {
        const newValue =
          op === DateCellFilterOperator.NOT_SET ? undefined : value
        props.onModelChange(buildModel(op, newValue))
      }}
      onChangeDateValue={val => props.onModelChange(buildModel(operator, val))}
      clearFilter={() => props.onModelChange(null)}
    />
  )
}
